<template>
    <v-container class="pa-5" fluid>
        <RoomReservationDialog
            :is-open="roomReservationDialog"
            :reservation-data="currentReservation"
            @on-close="closeRoomReservationDialog"
        />
        <page-title>Salki</page-title>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <FullCalendar
                        ref="fullCalendar"
                        class="fc fc-ltr fc-unthemed fc-colours"
                        :options="{
                            ...calendarOptions,
                            resources: roomReservationsTypes,
                            events: roomReservations
                        }"
                    />
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import RoomReservationDialog from './RoomReservationDialog'

import calendar from '@/mixins/calendar'

import { errorHandler } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'

import { orderBy } from 'lodash'
import FullCalendar from '@fullcalendar/vue'
import interaction from '@fullcalendar/interaction'
import resourceTimeline from '@fullcalendar/resource-timeline'
import tippy from 'tippy.js'
import { appConfig } from '@/config'

export default {
    name: 'RoomReservationsList',
    components: {
        PageTitle,
        FullCalendar,
        RoomReservationDialog
    },
    mixins: [ calendar ],
    beforeRouteLeave(to, from, next) {
        this.$store.dispatch('administration/clearRoomReservations')
        next()
    },
    data() {
        return {
            calendarOptions: {
                initialView: 'resourceTimelineDay',
                plugins: [interaction, resourceTimeline],
                schedulerLicenseKey: appConfig.common.schedulerLicenseKey,
                headerToolbar: {
                    left: 'prev, next, today',
                    center: 'title',
                    right: 'resourceTimelineDay, resourceTimelineTwoDay, resourceTimelineWeek, next',
                },
                views: {
                    resourceTimelineTwoDay: {
                        type: 'resourceTimeline',
                        duration: { days: 2 },
                        buttonText: '2 dni'
                    }
                },
                customButtons: {
                    prev: {
                        text: 'prev',
                        click: () => this.getDataOnWeekChangeButton('prev')
                    },
                    next: {
                        text: 'next',
                        click: () => this.getDataOnWeekChangeButton('next')
                    },
                    today: {
                        text: 'Dziś',
                        click: () => this.getTodaysReservations()
                    }
                },
                firstDay: 1,
                locale: 'pl',
                buttonText:
                {
                    week:     'Tydzień',
                    day:      'Dzień',
                },
                resourceAreaColumns: [
                    {
                        headerContent: 'Salki'
                    }
                ],
                businessHours: {
                    daysOfWeek: [ 1, 2, 3, 4, 5 ],
                    startTime: '08:00',
                    endTime: '16:00',
                },
                height: 'auto',
                editable: true,
                selectable: true,
                dateClick: this.showRoomReservationDialog,
                eventClick: this.showEditRoomReservationDialog,
                eventDidMount: this.eventRender,
                eventResize: this.eventResize,
                eventDrop: this.eventResize,
                eventOverlap: () => false,
                slotDuration: '00:15:00',
                resourceAreaWidth: '15%',
                scrollTime: '05:00'
            },
            currentReservation: {
                actionName: null,
                info: null,
                reservationData: {}
            },
            roomReservationDialog: false
        }
    },
    computed: {
        roomReservations() {
            return this.$store.state.administration.roomReservations.roomReservations
        },
        roomReservationsTypes() {
            return orderBy(this.$store.state.administration.roomReservations.roomReservationsTypes, 'codeName', 'asc')
        },
    },
    mounted() {
        this.getRoomReservations(this.currentWeekDateRange()),
        Promise.all([
            this.$store.dispatch('administration/getRoomReservationsTypes'),
            this.$store.dispatch('administration/getRoomReservationsStatuses')
        ])
            .catch(error => {
                errorHandler(error)
            })
    },
    methods: {
        getRoomReservations(weekRange) {
            this.$store.dispatch('administration/getRoomReservations', {
                query: `?startDate=${weekRange[0]}&endDate=${weekRange[weekRange.length-1]}`
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getTodaysReservations() {
            const fullCalendarApi = this.$refs.fullCalendar.getApi()

            fullCalendarApi.today()
            this.getRoomReservations(this.currentWeekDateRange())
        },
        getDataOnWeekChangeButton(direction) {
            this.$store.dispatch('administration/clearRoomReservations')
            const fullCalendarApi = this.$refs.fullCalendar.getApi()

            direction === 'next' ? fullCalendarApi.next() : fullCalendarApi.prev()
            const start = fullCalendarApi.view.activeStart
            const weekRange = this.generateWeekRange(start)

            this.getRoomReservations(weekRange)
        },
        updateCalendarView() {
            const fullCalendarApi = this.$refs.fullCalendar.getApi()
            const start = fullCalendarApi.view.activeStart
            const weekRange = this.generateWeekRange(start)

            this.getRoomReservations(weekRange)
        },
        showRoomReservationDialog(info) {
            if (hasUserPermission('room_reservations_create')) this.roomReservationDialog = true
            this.currentReservation = {
                actionName: 'create',
                info: info
            }
        },
        closeRoomReservationDialog() {
            this.roomReservationDialog = false
            setTimeout(() => {
                this.currentReservation = {
                    actionName: null,
                    info: null,
                    reservationData: {}
                }
            }, 500)
            this.updateCalendarView()
        },
        showEditRoomReservationDialog(info) {
            if (!info.event.extendedProps.isBlocked) {
                this.$store.dispatch('administration/getRoomReservation', info.event.id)
                    .then(response => {
                        this.currentReservation = {
                            actionName: 'update',
                            info: info,
                            reservationData: response.data
                        }
                        this.roomReservationDialog = true
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
            }
        },
        eventResize({ event }) {
            let roomReservation = {
                id: event.id,
                name: event._def.title,
                startDate: event.startStr,
                endDate: event.endStr,
                roomTypeId: event._def.resourceIds.at(0),
                roomReservationStatusId: event._def.extendedProps.roomReservationStatusId,
            }

            this.$store.dispatch('administration/updateRoomReservation', roomReservation )
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.updateCalendarView()
                })
        },
        eventRender(info) {
            const content = `
                <div class="v-application">
                    <div class="d-block">
                        <div class="mb-1 subtitle font-weight-bold">
                            ${info.event.extendedProps.description.reservationName}
                        </div>
                        <div>
                            Termin: <span class="font-weight-bold">${info.event.extendedProps.description.reservationDate}</span>
                        </div>
                        <div>
                            Rezerwujący:
                            <span class="font-weight-bold">${info.event.extendedProps.description.reservationCreatedBy}</span>
                        </div>
                        <div>
                            <label>Status:</label>
                            <span class="font-weight-bold">${info.event.extendedProps.description.reservationStatus}</span>
                        </div>
                    </div>
                </div>
            `

            tippy(info.el, {
                content: content,
                placement: 'top',
                theme: 'grey',
                delay: [300, 0],
                allowHTML: true
            })
        }
    }
}

</script>
